import { useState, useCallback } from 'react';
import { authApi } from '../api/authApi';

/**
 * Hook for validating user session on demand
 * Reads from localStorage and validates with the backend
 * 
 * @returns {Object} { isCheckingSession, loading, validateSession }
 * 
 * validateSession expects an object with:
 * @param {Object} callbacks
 * @param {Function} callbacks.onSuccess - Callback called on successful validation (user, serviceDesk) => void
 * @param {Function} callbacks.onError - Callback called on validation failure or error () => void
 */
export const useSessionValidation = () => {
  const [isCheckingSession, setIsCheckingSession] = useState(false);
  const [loading, setLoading] = useState(false);

  const validateSession = useCallback(async ({ onSuccess, onError }) => {
    setIsCheckingSession(true);
    setLoading(true);
    
    try {
      const storedName = localStorage.getItem('userName');
      const storedId = localStorage.getItem('userId');

      // No stored credentials
      if (!storedName || !storedId) {
        clearLocalStorage();
        if (onError) onError();
        setIsCheckingSession(false);
        setLoading(false);
        return { success: false };
      }

      // Validate with backend
      const data = await authApi.validateSession(storedName, storedId);

      if (data.success) {
        if (onSuccess) {
            onSuccess(data.user, data.serviceDesk);
        }
        setIsCheckingSession(false);
        setLoading(false);
        return { success: true, data };
      } else {
        clearLocalStorage();
        if (onError) {
          onError();
        }
        setIsCheckingSession(false);
        setLoading(false);
        return { success: false };
      }
    } catch (error) {
      clearLocalStorage();
      if (onError) {
        onError();
      }
      setIsCheckingSession(false);
      setLoading(false);
      return { success: false, error };
    }
  }, []);

  return { isCheckingSession, loading, validateSession };
};

/**
 * Clear all authentication-related data from localStorage
 */
const clearLocalStorage = () => {
  localStorage.removeItem('userName');
  localStorage.removeItem('userAccountId');
  localStorage.removeItem('userId');
  localStorage.removeItem('userAuthToken');
  localStorage.removeItem('selected-service-desk');
};

